import {
  commitFieldValue,
  FieldWrapper,
  getFieldErrorMessage,
} from "@birdwatch/ui/form";
import { setHours, setMinutes, setSeconds } from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import type { ReactNode } from "react";
import { Calendar } from "@/components/ui/calendar/calendar";
import { SelectClearFrame, SelectTrigger } from "@/components/ui/combobox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Typography } from "@/components/ui/typography";
import {
  type FieldBindingProps,
  PolyField,
  pickBinding,
} from "@/fields/_internal/poly-field";
import { DISPLAY_DATE_SHORT, formatDate } from "@/libs/date-fns";

type CalendarDateFieldBaseProps = {
  label?: ReactNode;
  /** Marks the label "(Optional)". */
  optional?: boolean;
  /** An action at the right end of the label row. */
  labelAction?: ReactNode;
  /** An X in the trigger clears the date (to `null`), committed like a pick. */
  clearable?: boolean;
  disabled?: boolean;
  /** Applied to the FieldWrapper root. */
  className?: string;
  /** Applied to the trigger. */
  inputClassName?: string;
  /** Trigger text when the value is null. Default "Pick a date". */
  placeholder?: string;
  /** Required — the tz the picked calendar day is interpreted in. */
  timeZone: string;
  /**
   * Normalize the picked calendar day to this LOCAL wall-clock time before
   * storing. Omit to store the day exactly as the Calendar emits it.
   * The product rule ("tasks are due at 18:00 local") lives at the CALL SITE,
   * not here.
   */
  timeOfDay?: { hours: number; minutes: number };
  /** date-fns token from `@/libs/date-fns` for the trigger. Default DISPLAY_DATE_SHORT. */
  displayFormat?: string;
  /** Leading icon inside the trigger. */
  icon?: ReactNode;
  // inline-edit pop-out controls
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
};

export type CalendarDateFieldProps = CalendarDateFieldBaseProps &
  FieldBindingProps;

/** Pure: snap `date` to `timeOfDay` in `timeZone`. Exported for tests. */
export function applyTimeOfDay(
  date: Date,
  timeZone: string,
  timeOfDay?: { hours: number; minutes: number },
): Date {
  if (!timeOfDay) return date;
  const inZone = toZonedTime(date, timeZone);
  const snapped = setSeconds(
    setMinutes(setHours(inZone, timeOfDay.hours), timeOfDay.minutes),
    0,
  );
  return fromZonedTime(snapped, timeZone);
}

export function CalendarDateField(props: CalendarDateFieldProps) {
  const {
    label,
    optional,
    labelAction,
    clearable = false,
    disabled,
    className,
    inputClassName,
    placeholder = "Pick a date",
    timeZone,
    timeOfDay,
    displayFormat = DISPLAY_DATE_SHORT,
    icon,
    defaultOpen,
    onOpenChange,
    modal = true,
  } = props;
  const binding = pickBinding(props);
  return (
    <PolyField<Date | null> {...binding}>
      {(field) => {
        const value = field.state.value ?? null;
        return (
          <FieldWrapper
            label={label}
            optional={optional}
            labelAction={labelAction}
            error={getFieldErrorMessage(field)}
            className={className}
            disabled={disabled}
          >
            <Popover
              modal={modal}
              defaultOpen={defaultOpen}
              onOpenChange={onOpenChange}
            >
              <SelectClearFrame
                onClear={
                  clearable ? () => commitFieldValue(field, null) : undefined
                }
                clearLabel="Clear date"
                isEmpty={!value}
                disabled={disabled}
              >
                <PopoverTrigger disabled={disabled} className="group" asChild>
                  <SelectTrigger
                    isEmpty={!value}
                    disabled={disabled}
                    className={inputClassName}
                    hasClear={clearable}
                  >
                    {icon}
                    <Typography
                      size="sm"
                      className="line-clamp-1 break-all grow"
                    >
                      {value
                        ? formatDate(
                            value.toISOString(),
                            displayFormat,
                            timeZone,
                          )
                        : placeholder}
                    </Typography>
                  </SelectTrigger>
                </PopoverTrigger>
              </SelectClearFrame>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={value ?? undefined}
                  defaultMonth={value ?? undefined}
                  onSelect={(date) => {
                    if (!date) return;
                    // Discrete picker — picking IS committing (policy table).
                    commitFieldValue(
                      field,
                      applyTimeOfDay(date, timeZone, timeOfDay),
                    );
                  }}
                  timeZone={timeZone}
                  fixedWeeks
                />
              </PopoverContent>
            </Popover>
          </FieldWrapper>
        );
      }}
    </PolyField>
  );
}
